import { FC, useCallback, useMemo, useState } from 'react'
import { getV3ColorCssVariable } from '../../../design-system/colors_v3'
import { SelectOption } from '../../../model/SelectOption'
import {
  DropdownItem,
  Heading,
  Icon,
  Modal,
  ModalActions,
  ModalContent,
  RoundedButton,
  SelectInput,
  Switch,
  ThinModalLayout,
} from '../../shared'
import { EfficienceDmpUploadModalProps } from './EfficienceDmpUploadModal.model'
import styles from './EfficienceDmpUploadModal.module.scss'

export const EfficienceDmpUploadModal: FC<EfficienceDmpUploadModalProps> = ({
  isUnmounting,
  healthcareSettings,
  onValid,
  onClose,
  isDeletion = false,
}) => {
  const [hideToPatient, setHideToPatient] = useState(false)
  const [hideToLegalRepresentative, setHideToLegalRepresentative] = useState(false)
  const [hideToHealthProfessional, setHideToHealthProfessional] = useState(false)
  const [selectSetting, setSelectedSetting] = useState<SelectOption<string>>()

  const healthcareSettingsOptions = useMemo(
    () =>
      healthcareSettings.map(({ code, description }) => ({
        value: code,
        label: description,
      })),
    [healthcareSettings],
  )

  const handleValid = useCallback(() => {
    if (selectSetting) {
      setHideToPatient(false)
      setHideToLegalRepresentative(false)
      setHideToHealthProfessional(false)
      setSelectedSetting(undefined)
      onValid({
        hideToPatient,
        hideToLegalRepresentative,
        hideToHealthProfessional,
        healthcareSettingsId: selectSetting.value,
      })
    }
  }, [selectSetting, hideToHealthProfessional, hideToLegalRepresentative, hideToPatient, onValid])

  return (
    <Modal isUnmounting={isUnmounting}>
      <ThinModalLayout size="medium">
        <ModalContent>
          <Icon size="mega" icon="folderUser" color={getV3ColorCssVariable('shades', 'shade2')} />
          <Heading size={3}>
            {isDeletion
              ? 'Supprimer le document du DMP'
              : 'Valider et envoyer le document sur le DMP'}
          </Heading>
          <SelectInput
            title="Cadre de soin"
            value={selectSetting}
            options={healthcareSettingsOptions}
            onSelect={setSelectedSetting}
            renderOption={({ label }, _, isHovered) => (
              <DropdownItem selected={isHovered}>
                <span className={styles.dmpUpdloadModalHealthCareSettingsOption}>{label}</span>
              </DropdownItem>
            )}
            testId="dmp-healthcare_settings"
          />
          {!isDeletion && (
            <div className={styles.dmpUpdloadModalHealthCareHideOptions}>
              <Switch
                name="Cacher au patient"
                checked={hideToPatient}
                disabled={hideToHealthProfessional}
                onChange={setHideToPatient}
                testId="switch-efficience-dmp-patient-hide"
              />
              <Switch
                name="Cacher au représentant légal"
                checked={hideToLegalRepresentative}
                onChange={setHideToLegalRepresentative}
                testId="switch-efficience-dmp-patient_legal_representative-hide"
              />
              <Switch
                name="Cacher aux professionnels de santé"
                checked={hideToHealthProfessional}
                disabled={hideToPatient}
                onChange={setHideToHealthProfessional}
                testId="switch-efficience-dmp-health_professionals-hide"
              />
            </div>
          )}
        </ModalContent>
        <ModalActions>
          <div className={styles.dmpUpdloadModalHealthCareActionButtons}>
            <RoundedButton onClick={onClose} label="Annuler" testId="button-dmp_documents-cancel" />
            <RoundedButton
              disabled={!selectSetting}
              onClick={handleValid}
              label={isDeletion ? 'Supprimer' : 'Valider'}
              testId="button-dmp_documents-send"
            />
          </div>
        </ModalActions>
      </ThinModalLayout>
    </Modal>
  )
}
